import {
  COMMUNICATION_KINDS,
  describeEvent,
  humanizeEvent,
  isDealEvent,
} from './activityNarrative'
import type { DealFieldDef } from '@/services/dealFile'
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import {
  catalogueValue,
  formatAmount,
  formatDate,
  formatMoney,
  formatMultiple,
  formatPct,
  formatPercentField,
  formatPoints,
  isPresent,
  textOrDash,
} from '@/components/deal/dossier/format'
import { ltvWithImplied } from '@/components/deal/dossier/valueBasis'
import { ADDRESS_PENDING_TEXT } from '@/components/deal/dossier/assetCoordinate'
import type { DealAttribution } from '@/services/dossierContext'
import { stateOfLineage } from '@/components/fields/lineage'
import type {
  CapitalStackRow,
  DossierWorkspaceModel,
  FieldSourceRef,
  KpiMetric,
  KpiMetricId,
  MetricLineage,
  WorkspaceBuildInput,
} from './types'
import { buildPipelineStages } from './stages'
import { buildDocumentsModel } from '@/components/deal/dossier/documentsModel'
import { stageAuditEntries } from '@/components/deal/dossier/stageAuditEntries'
import { buildCapitalStackModel } from '@/components/deal/dossier/capitalStackModel'
import {
  committeeDecision,
  NOT_YET_APPROVED,
  productField,
  SENIOR_NOT_STATED,
  seniorAheadUnstated,
  termLabel,
  unitCountLabel,
} from '@/components/deal/dossier/dealTerms'
import { ADDRESS_CONFIRM_REASON, ADDRESS_EDIT_TARGETS, type EditTarget } from '@/components/deal/dossier/editableFields'

const DASH = '—'

/** The product label, marked "(default)" when it was read from a field whose value
 *  is only the platform's entry default (GET /loans `platform_defaults`). */
function productWithDefaultMark(
  get: Parameters<typeof productField>[0],
  loan: WorkspaceBuildInput['loan'],
): string | null | undefined {
  const field = productField(get)
  if (!field) return loan?.product
  return (loan?.platformDefaults ?? []).includes(field.fieldName) ? `${field.value} (default)` : field.value
}

/**
 * The fields each composed Overview row is edited as, once the dossier is
 * unlocked. The row prints one string made of several fields; an edit to that
 * string could only be split back into fields by guessing, so the row offers
 * the fields themselves (see EditableValue).
 */
const BUILT_EDIT: EditTarget[] = [
  { name: 'year_built', label: 'Year Built', kind: 'number' },
  { name: 'year_renovated', label: 'Year Renovated', kind: 'number' },
]
const UNITS_EDIT: EditTarget[] = [
  { name: 'units', label: 'Number of Units', kind: 'count' },
  // "412 Units" reads as one count and its measure: no separator between them.
  { name: 'unit_of_measure', label: 'Unit of Measure', kind: 'select', separator: '' },
]
function productTermEdit(g: (name: string) => unknown): EditTarget[] {
  // The legacy `product` key where that is what the deal holds; otherwise the
  // catalogue's Loan Type. `capital_program` is a closed list with its own
  // header slot, never edited here as a product.
  const product = productField(g)?.fieldName === 'product' ? 'product' : 'loan_type'
  return [
    { name: product, label: 'Product / Loan Type', kind: 'text' },
    { name: 'term_months', label: 'Initial Term (months)', kind: 'number' },
    { name: 'extensions', label: 'Extensions (months)', kind: 'number' },
  ]
}

/** The Address row's chip when the settled address (not the raw `location`) is printed. */
const ADDRESS_SOURCE_CHIPS: Record<'person' | 'package' | 'master', FieldSourceRef> = {
  person: {
    label: 'Entered by a person',
    detail: 'The address was typed or confirmed on this deal.',
    fieldName: 'location',
    attribution: 'client',
    disputed: false,
    state: 'confirmed',
  },
  package: {
    label: 'Stated in the package',
    detail: 'The address is what the deal package states.',
    fieldName: 'location',
    attribution: null,
    disputed: false,
    state: 'stated',
  },
  master: {
    label: 'Property master',
    detail: 'The address is the property record\'s; the deal states none of it.',
    fieldName: 'location',
    attribution: null,
    disputed: false,
    state: null,
  },
}

/** The lineage `method` of a portfolio's figure rolled up from its properties'
 *  sponsor models (`sponsor_figures.ROLLUP_METHOD` on the server). */
export const PORTFOLIO_ROLLUP_METHOD = 'portfolio_rollup'

function lineageMap(fields: LoanFieldLineageField[]): Map<string, LoanFieldLineageField> {
  return new Map(fields.map((f) => [f.fieldName, f]))
}

function sourceFromLineage(
  fieldName: string | null,
  lineage: Map<string, LoanFieldLineageField>,
  attribution?: DealAttribution | null,
): FieldSourceRef | null {
  if (!fieldName) return null
  const row = lineage.get(fieldName)
  const slot = attribution?.byField?.[fieldName]
  // The record shows the value that governs, and a person's decision governs
  // over a parsed one — so a field the firm has concluded on is the firm's,
  // even though an outside party also stated it. The disagreement is not
  // lost: it is what `disputed` says.
  const outside = slot?.sponsor ?? slot?.broker ?? slot?.external ?? null
  const authored = slot?.client ?? outside
  const state = row ? stateOfLineage(row) : null
  // A blank value carries no chip: "SPONSOR" on an "LTV —" said the sponsor
  // stated a figure nobody has (OVR-25).
  if (state === 'missing' || state === 'not_applicable') return null
  const label = row?.summary ?? row?.source ?? authored?.sourceDoc ?? null
  if (!label) return null
  // Whose figure: for a stated value, whose statement the lineage names —
  // SPONSOR only when the sponsor's own model is the source. A confirmed
  // value is the firm's; a derived or suggested one is nobody's statement.
  const whose =
    state === 'stated'
      ? row?.provenance?.attribution ?? authored?.attribution ?? null
      : state === 'confirmed'
        ? 'client'
        : state
          ? null
          : authored?.attribution ?? null
  return {
    label,
    detail: row?.reason ?? authored?.why ?? null,
    fieldName,
    attribution: whose,
    cellRef: row?.provenance?.cellRef ?? authored?.cellRef ?? null,
    // A portfolio's figure the server rolled up from its properties' sponsor
    // models: the sponsor's, by sum, not one model's cell.
    rollup: state === 'stated' && row?.provenance?.method === PORTFOLIO_ROLLUP_METHOD,
    disputed: Boolean(outside && slot?.client && outside.value !== slot.client.value),
    state,
    formula: row?.derivation?.formula ?? null,
  }
}

/** A figure this screen computes from other fields and that has no catalogue
 *  field of its own — Derived, with its formula, and nobody's statement. The
 *  Basis ($/SF) row took the property value's SPONSOR badge (PKG-17). */
function screenDerived(fieldName: string, formula: string): FieldSourceRef {
  return {
    label: `Derived: ${formula}`,
    detail: 'Computed on this page from the deal\'s own figures.',
    fieldName,
    attribution: null,
    cellRef: null,
    disputed: false,
    state: 'derived',
    formula,
  }
}

function formatBasis(value: unknown, sf: unknown, units?: unknown, measure?: unknown): string {
  const v = typeof value === 'number' ? value : Number(value)
  if (value === null || value === undefined || value === '' || !Number.isFinite(v)) return DASH
  const area = typeof sf === 'number' ? sf : Number(sf)
  if (sf !== null && sf !== undefined && sf !== '' && Number.isFinite(area) && area > 0) {
    return `$${Math.round(v / area).toLocaleString()}/SF`
  }
  // A multifamily or hotel deal states units or keys, not square feet; its
  // basis is per unit. SF-only left Basis blank on every such deal.
  const count = typeof units === 'number' ? units : Number(units)
  const per = String(measure ?? '').trim().toLowerCase()
  if (units === null || units === undefined || units === '' || !Number.isFinite(count) || count <= 0 || per === 'sf') {
    return DASH
  }
  const noun = per === 'rooms' || per === 'keys' ? 'key' : per === 'beds' ? 'bed' : per === 'pads' ? 'pad' : 'unit'
  return `$${Math.round(v / count).toLocaleString()}/${noun}`
}

/** "2016 / 2022", "2016", or "Renovated 2022" — built and renovated as stated. */
function builtAndRenovated(built: unknown, renovated: unknown): string {
  const b = isPresent(built) ? String(built) : null
  const r = isPresent(renovated) ? String(renovated) : null
  if (b && r) return `${b} / ${r}`
  if (b) return b
  if (r) return `Renovated ${r}`
  return DASH
}

function creditDecision(stage: unknown): string {
  const s = String(stage ?? '')
  if (s === 'IC / Committee Approved') return 'Approved with conditions'
  if (s === 'Funded') return 'Approved'
  if (s.includes('Term Sheet')) return 'In underwriting'
  if (s.includes('Quote') || s.includes('Pre-screen')) return 'Quoted'
  return 'Pending'
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.replace(/[$,%\s,]/g, ''))
    return Number.isFinite(n) ? n : null
  }
  return null
}

function severityOf(raw: string | undefined): 'high' | 'medium' | 'low' | null {
  const s = (raw ?? '').toLowerCase()
  if (s === 'high' || s === 'critical') return 'high'
  if (s === 'medium' || s === 'moderate') return 'medium'
  if (s === 'low') return 'low'
  return null
}

function pctChange(entry: number, current: number): { text: string; direction: 'up' | 'down' | 'flat' } {
  if (entry === 0) return { text: DASH, direction: 'flat' }
  const delta = current - entry
  const pct = (delta / Math.abs(entry)) * 100
  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
  const sign = delta > 0 ? '+' : ''
  return {
    text: `${sign}${formatMoney(delta)} (${sign}${pct.toFixed(1)}%)`,
    direction,
  }
}

/**
 * The catalogue's `property_value` is the AS-IS value (catalogue label "Value
 * (as-is)"), whatever produced it — a stated as-is value, a stated appraisal, a
 * component valuation. It was printed as "Appraisal Value" / "Value
 * (Appraised)" on ~15 deals whose packages contain no appraisal, where the
 * figure was loan / LTV, NOI / cap rate or a purchase price routed round that
 * loop. One label everywhere, and never "Appraisal" unless an appraisal is the
 * source.
 */
export const VALUE_LABEL = 'Value (As-Is)'

const METRIC_FORMULAS: Partial<Record<KpiMetricId, { formula: string; inputs: { label: string; field: string }[] }>> = {
  ltv: {
    formula: `Loan Amount ÷ ${VALUE_LABEL}`,
    inputs: [
      { label: 'Loan Amount', field: 'loan_amount' },
      { label: VALUE_LABEL, field: 'property_value' },
    ],
  },
  ltc: {
    formula: 'Loan Amount ÷ Total Cost',
    inputs: [
      { label: 'Loan Amount', field: 'loan_amount' },
      { label: 'Total Cost', field: 'total_cost' },
    ],
  },
  dscr: {
    formula: 'NOI ÷ Annual Debt Service',
    inputs: [
      { label: 'NOI', field: 'noi' },
      { label: 'Annual Debt Service', field: 'annual_debt_service' },
    ],
  },
  debt_yield: {
    formula: 'NOI ÷ Loan Amount',
    inputs: [
      { label: 'NOI', field: 'noi' },
      { label: 'Loan Amount', field: 'loan_amount' },
    ],
  },
  basis: {
    formula: `${VALUE_LABEL} ÷ Rentable SF`,
    inputs: [
      { label: VALUE_LABEL, field: 'property_value' },
      { label: 'Rentable SF', field: 'sf' },
    ],
  },
}

function cashBlob(raw: WorkspaceBuildInput['raw']): Record<string, unknown> {
  const direct = (raw as Record<string, unknown>).cash_flows
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct as Record<string, unknown>
  }
  const custom = raw.custom_fields
  if (custom && typeof custom === 'object') {
    const cash = (custom as Record<string, unknown>).cash_flows
    if (cash && typeof cash === 'object' && !Array.isArray(cash)) {
      return cash as Record<string, unknown>
    }
  }
  return {}
}

function readCreditNarrative(raw: WorkspaceBuildInput['raw']): {
  strengths: string[]
  risks: DossierWorkspaceModel['risks']
} {
  const blob = cashBlob(raw)
  const source = blob.credit_narrative ?? blob.creditNarrative
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return { strengths: [], risks: [] }
  }
  const narrative = source as Record<string, unknown>
  const strengths = (Array.isArray(narrative.strengths) ? narrative.strengths : [])
    .flatMap((entry) => {
      if (typeof entry === 'string' && entry.trim()) return [entry.trim()]
      if (!entry || typeof entry !== 'object') return []
      const text = String((entry as Record<string, unknown>).text ?? '').trim()
      return text ? [text] : []
    })
  const risks = (Array.isArray(narrative.risks) ? narrative.risks : []).flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const row = entry as Record<string, unknown>
    const risk = String(row.text ?? row.risk ?? '').trim()
    if (!risk) return []
    const mitigation = String(row.mitigant ?? row.mitigation ?? '').trim()
    return [{ risk, mitigation: mitigation || DASH, severity: null }]
  })
  return { strengths, risks }
}

function formatFieldValue(name: string, value: unknown, defs: DealFieldDef[]): string {
  const def = defs.find((d) => d.name === name)
  if (!isPresent(value)) return DASH
  if (def?.kind === 'currency') return formatMoney(value)
  if (def?.kind === 'percent') return formatPercentField(value, name)
  if (name === 'loan_amount' || name === 'property_value' || name === 'total_cost') return formatAmount(value)
  if (name === 'dscr') return formatMultiple(value)
  if (name === 'sf') return Number(value).toLocaleString()
  return textOrDash(value)
}

export function buildWorkspaceModel(
  input: WorkspaceBuildInput & {
    /**
     * The address the dossier settled on (`resolveDealAddress`, withholding
     * applied) — '' when nothing stated one. The raw street, city and location
     * on the deal file are not read for the Overview's Address row when this is
     * given: on a legacy deal they are the building's name resolved by Google
     * Places, which is exactly what the resolution withholds.
     */
    address?: string
    /**
     * Whose statement the settled address is (`ResolvedAddress.source`). The
     * Address row's source chip follows it: the raw `location` lineage row names
     * the event that wrote the deal file's text, which on a legacy deal is a
     * Google Places search, not the package city the row now shows.
     */
    addressSource?: 'person' | 'package' | 'master' | null
    /** The dossier context that settles the address has not answered yet. */
    addressPending?: boolean
    /**
     * The address may be edited as its street, city, state and postal code:
     * a street a document or a person stands behind is on screen. False when
     * the header says "Address not stated in package", "street not stated" or
     * "could not be checked" — the deal file's street and city may then be
     * the ones a Places name lookup wrote (Washington Square at the Monument),
     * and an editor pre-filled with them would show that guess as the deal's
     * street, and a keystroke in it would assert it. The header's "confirm
     * address" flow, which never offers the guess, is the way to state one.
     * Absent: not editable.
     */
    addressEditable?: boolean
    /**
     * The person's unsaved value for a field, while they are editing — so the
     * text the header facts line and the composed Overview rows print follows
     * the draft the popover and the metric bar already show. Saved Purpose
     * "Acquisition" kept printing beside a draft of "Refinance" until Save.
     * Null when the field is not being edited.
     */
    drafted?: (name: string) => { value: unknown } | null
  },
): DossierWorkspaceModel {
  const { raw, defs, loan, lineage, segments, documents, sourceFiles, activity, notes, triggers } = input
  const lineageByField = lineageMap(lineage)
  const attribution = input.attribution ?? null
  const v = input.effectiveValue
  const g = (name: string) => {
    const typed = input.drafted?.(name)
    if (typed) return isPresent(typed.value) ? typed.value : undefined
    return catalogueValue(raw, defs, name)
  }

  const loanId = String(raw.id ?? loan?.id ?? '')
  const propertyName = textOrDash(g('property_name') ?? loan?.property)
  // The street address when the record carries one, so Overview's Address row
  // matches the header rather than falling back to "City, ST" beside it.
  const streetAddress = [
    g('street'),
    [g('city'), g('state')].filter(isPresent).join(', '),
    g('zip'),
  ]
    .filter(isPresent)
    .map((part) => String(part).trim())
    .filter(Boolean)
    .join(', ')
  const address = textOrDash(
    input.address !== undefined ? input.address : streetAddress || g('location') || loan?.location,
  )
  // The chip follows the address that is printed. Only a caller that never
  // settled one prints the deal file's text, and the lineage of `location` is
  // then the lineage of what is shown.
  const addressSource: FieldSourceRef | null =
    input.address === undefined
      ? sourceFromLineage('location', lineageByField, attribution)
      : input.addressSource
        ? ADDRESS_SOURCE_CHIPS[input.addressSource]
        : null
  const assetType = textOrDash(g('asset_type') ?? loan?.type)
  const assignee = textOrDash(g('assignee') ?? loan?.assignee, '')
  const stage = g('stage') ?? loan?.stage
  const originated = g('originated') ?? loan?.originated
  const priority = textOrDash(g('priority') ?? loan?.priority, '')

  const sf = v('sf')
  const units = v('units')
  const unitMeasure = v('unit_of_measure')
  const propertyValue = v('property_value')
  // `loans.loan_amount` is NOT NULL, so 0 is how a deal records that no
  // package stated a loan amount (Cranbury states '65% LTV' and no dollar
  // figure). It is never a request for $0.
  const loanAmount = asNumber(v('loan_amount')) === 0 ? null : v('loan_amount')
  // The request on the pipeline's one basis: the total commitment where the
  // split is recorded, else the loan amount as the whole commitment. Calloway's
  // loan_amount is its $68M initial advance of $71.12M; the pipeline read
  // $71.12M and this header "Request: $68M" (OVR-19).
  const initialFunding = asNumber(v('initial_funding'))
  const futureFunding = asNumber(v('future_funding'))
  const commitment = asNumber(v('fully_funded_amount'))
    ?? (initialFunding != null && futureFunding != null ? initialFunding + futureFunding : undefined)
  // Where the stated loan amount IS the commitment (The Marlowe: $24.5MM
  // stated, $21.6M + $2.9M derived), the request cites the statement rather
  // than the derivation that reproduces it (nd-022).
  const loanIsCommitment =
    commitment != null && asNumber(loanAmount) != null && Math.abs((asNumber(loanAmount) as number) - commitment) < 1
  const requested = commitment ?? loanAmount
  const requestedField = commitment != null && !loanIsCommitment ? 'fully_funded_amount' : 'loan_amount'
  // A commitment no field states is this page's sum of the two advances, and
  // says so. It was printed bare under the `fully_funded_amount` field it does
  // not hold, so unlocking turned Calloway's "$71.12M" into an empty box for a
  // total — a figure typed there would no longer follow initial + future
  // funding. As a derived figure it opens those two inputs instead (R1).
  const requestedSummed =
    requestedField === 'fully_funded_amount' && asNumber(v('fully_funded_amount')) == null
  const requestedSource = requestedSummed
    ? sourceFromLineage('fully_funded_amount', lineageByField, attribution)
      ?? screenDerived('fully_funded_amount', 'initial funding + future funding')
    : sourceFromLineage(requestedField, lineageByField, attribution)
  const requestedInputs = requestedSummed ? ['initial_funding', 'future_funding'] : undefined
  // A price per SF / per unit the deal records — stated (Cranbury "$118/SF")
  // or derived — is the Basis; otherwise value / SF or value / unit. Reading
  // only the value left Cranbury's stated $118/SF as "—" (nd-004).
  const recordedPsf = asNumber(v('price_per_sf'))
  const recordedPpu = asNumber(v('price_per_unit'))
  const basis = recordedPsf != null && recordedPsf > 0
    ? `$${Math.round(recordedPsf).toLocaleString()}/SF`
    : recordedPpu != null && recordedPpu > 0 && !isPresent(sf)
      ? formatBasis(recordedPpu, null, 1, unitMeasure)
      : formatBasis(propertyValue, sf, units, unitMeasure)
  // The recorded LTV, and beside it the one the deal's own loan and value
  // imply when they differ (R3 keeps the stated ratio; R4 shows the gap) —
  // the same figure the metric bar prints. 45 Park Ave's Overview read a bare
  // "LTV 58.00%" beside a $44.92M value and a 69.0% senior slice.
  const ltvText = ltvWithImplied(v('ltv'), loanAmount, propertyValue)
  // The Basis figure is value / SF, or value / unit where no SF is stated. It
  // carries its OWN source (a derived figure), never the property value's
  // sponsor badge (PKG-17 / OVR-25).
  const basisBySf = (recordedPsf != null && recordedPsf > 0) || isPresent(sf)
  const basisField = basisBySf ? 'price_per_sf' : 'price_per_unit'
  const basisFormula = basisBySf ? 'value / SF' : 'value / unit'
  const basisSource = sourceFromLineage(basisField, lineageByField, attribution) ?? screenDerived(basisField, basisFormula)
  // A debt yield on a subordinate note alone ignores the debt ahead of it.
  // Withheld, the row is not the `debt_yield` field either: named as that
  // field, the unlocked Overview put the note-only figure the row withholds
  // back on screen in an input. Economics drops its field name the same way.
  const debtYieldWithheld = seniorAheadUnstated(v, raw.capital_stack)
  const debtYieldText = debtYieldWithheld ? SENIOR_NOT_STATED : formatPoints(v('debt_yield'))
  const debtYieldField = debtYieldWithheld ? null : 'debt_yield'
  // An approval exists only once a committee has decided; before that the
  // request is a request, not an approved amount.
  const approval = committeeDecision(v, stage)
  const approvedText = !approval.decided
    ? NOT_YET_APPROVED
    : approval.approvedAmount != null
      ? formatAmount(approval.approvedAmount)
      : 'Not recorded'
  const maxLtvText = !approval.decided
    ? NOT_YET_APPROVED
    : approval.maxLtv != null
      ? formatPct(approval.maxLtv)
      : 'Not recorded'

  const headerMeta = [
    { label: 'Request', value: formatAmount(requested), fieldName: requestedField, source: requestedSource, inputs: requestedInputs },
    { label: 'Asset Type', value: assetType, fieldName: 'asset_type', source: sourceFromLineage('asset_type', lineageByField, attribution) },
    { label: 'Purpose', value: textOrDash(g('purpose')), fieldName: 'purpose', source: sourceFromLineage('purpose', lineageByField, attribution) },
    { label: 'Priority', value: priority || DASH, fieldName: 'priority', source: sourceFromLineage('priority', lineageByField, attribution) },
    { label: 'State', value: textOrDash(stage), fieldName: 'stage', source: sourceFromLineage('stage', lineageByField, attribution) },
    {
      // Never the maturity date: a five-year maturity is not a closing date.
      label: 'Target Close',
      value: formatDate(g('target_close')),
      fieldName: 'target_close',
      source: sourceFromLineage('target_close', lineageByField, attribution),
    },
  ]

  const overdueCount = triggers.filter((t) => t.status === 'overdue').length
  const openTriggers = triggers.filter((t) => t.status !== 'resolved')
  const clearedTriggers = triggers.filter((t) => t.status === 'resolved')

  const kpis: KpiMetric[] = [
    { id: 'requested', label: 'Requested', value: formatAmount(requested), sublabel: null, fieldName: requestedField, source: requestedSource, selectable: true },
    { id: 'approved', label: 'Approved', value: approvedText, sublabel: null, fieldName: 'approved_amount', source: approval.decided ? sourceFromLineage('approved_amount', lineageByField, attribution) : null, selectable: approval.decided },
    { id: 'total_cost', label: 'Total Cost', value: formatAmount(v('total_cost')), sublabel: null, fieldName: 'total_cost', source: sourceFromLineage('total_cost', lineageByField, attribution), selectable: true },
    { id: 'value', label: VALUE_LABEL, value: formatAmount(propertyValue), sublabel: null, fieldName: 'property_value', source: sourceFromLineage('property_value', lineageByField, attribution), selectable: true },
    { id: 'ltv', label: 'LTV', value: ltvText, sublabel: input.derivationNotes.ltv ?? null, fieldName: 'ltv', source: sourceFromLineage('ltv', lineageByField, attribution), selectable: true },
    { id: 'ltc', label: 'LTC', value: formatPct(v('ltc')), sublabel: input.derivationNotes.ltc ?? null, fieldName: 'ltc', source: sourceFromLineage('ltc', lineageByField, attribution), selectable: true },
    { id: 'dscr', label: 'DSCR', value: formatMultiple(v('dscr')), sublabel: input.derivationNotes.dscr ?? null, fieldName: 'dscr', source: sourceFromLineage('dscr', lineageByField, attribution), selectable: true },
    { id: 'debt_yield', label: 'Debt Yield', value: debtYieldText, sublabel: input.derivationNotes.debt_yield ?? null, fieldName: debtYieldField, source: sourceFromLineage(debtYieldField, lineageByField, attribution), selectable: true },
    { id: 'basis', label: 'Basis', value: basis, sublabel: input.derivationNotes[basisField] ?? basisFormula, fieldName: basisField, source: basisSource, selectable: true },
    { id: 'coupon', label: 'Coupon', value: textOrDash(g('rate') ?? loan?.rate), sublabel: null, fieldName: 'rate', source: sourceFromLineage('rate', lineageByField, attribution), selectable: false },
    { id: 'credit_decision', label: 'Credit Decision', value: creditDecision(stage), sublabel: null, fieldName: null, source: null, selectable: false },
    { id: 'blocker', label: 'Blocker', value: overdueCount > 0 ? String(overdueCount) : '0', sublabel: overdueCount > 0 ? 'Overdue items' : null, fieldName: null, source: null, selectable: false },
  ]

  const loanOverview = [
    { label: 'Requested Amount', value: formatAmount(requested), fieldName: requestedField, source: requestedSource, inputs: requestedInputs },
    { label: 'Basis', value: basis, fieldName: basisField, source: basisSource },
    { label: 'LTV', value: ltvText, fieldName: 'ltv', source: sourceFromLineage('ltv', lineageByField, attribution) },
    { label: 'LTC', value: formatPct(v('ltc')), fieldName: 'ltc', source: sourceFromLineage('ltc', lineageByField, attribution) },
    { label: 'DSCR', value: formatMultiple(v('dscr')), fieldName: 'dscr', source: sourceFromLineage('dscr', lineageByField, attribution) },
    { label: 'Debt Yield', value: debtYieldText, fieldName: debtYieldField, source: sourceFromLineage(debtYieldField, lineageByField, attribution) },
    { label: 'Coupon', value: textOrDash(g('rate') ?? loan?.rate), fieldName: 'rate', source: sourceFromLineage('rate', lineageByField, attribution) },
    {
      // The catalogue holds the term as `term_months`; `product` and `term`
      // are not catalogue fields, so this row was blank on every intake deal.
      label: 'Product / Term',
      // loan_type first: a saved Loan Type edit shows here as on Economics.
      // A program that is only the platform's entry default says so: the row's
      // source link is the term's and must not read as the broker stating it.
      value: textOrDash([productWithDefaultMark(g, loan), termLabel(g)].filter(isPresent).join(' · ')),
      fieldName: 'term_months',
      source: sourceFromLineage('term_months', lineageByField, attribution),
      edit: productTermEdit(g),
    },
  ]

  const waltField =
    (['avg_lease_term', 'walt', 'wale'] as const).find((name) => v(name) !== null && v(name) !== undefined)
    ?? 'avg_lease_term'
  const propertyFields = [
    { label: 'Property Type', value: assetType, fieldName: 'asset_type', source: sourceFromLineage('asset_type', lineageByField, attribution) },
    { label: 'Property Name', value: propertyName, fieldName: 'property_name', source: sourceFromLineage('property_name', lineageByField, attribution) },
    // While the context that settles the address is still on its way the row
    // says so: a dash reads as "the deal states none".
    // The address is edited as its parts: the server recomposes `location`
    // from them on every save, so a typed `location` would not survive.
    // Only an address something vouches for opens its parts (see
    // `addressEditable`); otherwise the row reads as it does locked and says
    // where the street is stated instead.
    input.addressPending
      ? { label: 'Address', value: ADDRESS_PENDING_TEXT, fieldName: 'location', source: null, edit: [], readOnlyReason: 'The address is still being settled.' }
      : {
          label: 'Address',
          value: address,
          fieldName: 'location',
          source: address === DASH ? null : addressSource,
          edit: input.addressEditable && address !== DASH ? ADDRESS_EDIT_TARGETS : [],
          readOnlyReason: input.addressEditable ? undefined : ADDRESS_CONFIRM_REASON,
        },
    { label: 'GBA / NRA', value: isPresent(sf) ? `${Number(sf).toLocaleString()} SF` : DASH, fieldName: 'sf', source: sourceFromLineage('sf', lineageByField, attribution) },
    { label: 'Units / Keys', value: textOrDash(unitCountLabel(v)), fieldName: 'units', source: sourceFromLineage('units', lineageByField, attribution), edit: UNITS_EDIT },
    { label: 'Occupancy', value: formatPct(v('occupancy')), fieldName: 'occupancy', source: sourceFromLineage('occupancy', lineageByField, attribution) },
    // The row is whichever of the three keys holds the figure, so unlocked it
    // edits that key: named `avg_lease_term` while showing `walt`, it turned a
    // stated WALT into an empty box.
    { label: 'WALE / WALT', value: textOrDash(v(waltField)), fieldName: waltField, source: sourceFromLineage(waltField, lineageByField, attribution) },
    { label: 'Year Built / Reno', value: builtAndRenovated(g('year_built') ?? loan?.yearBuilt, g('year_renovated')), fieldName: 'year_built', source: sourceFromLineage('year_built', lineageByField, attribution), edit: BUILT_EDIT },
    { label: VALUE_LABEL, value: formatAmount(propertyValue), fieldName: 'property_value', source: sourceFromLineage('property_value', lineageByField, attribution) },
    { label: 'NOI (In-Place)', value: formatMoney(v('noi')), fieldName: 'noi', source: sourceFromLineage('noi', lineageByField, attribution) },
    { label: 'Cap Rate', value: formatPct(v('cap_rate')), fieldName: 'cap_rate', source: sourceFromLineage('cap_rate', lineageByField, attribution) },
  ]

  // One capital-stack model for the Overview and the Capital Stack tab. The
  // Overview used to build its own single "Senior" tranche from the request,
  // ignoring the recorded equity and the stated position, so the two views
  // disagreed (Briar Hollow 100% senior here, 60/40 senior/equity there) and
  // a mezzanine note was drawn as the whole senior stack.
  // The equity's state comes from the same lineage the tab reads, so a total
  // equity the engine derived is not called "recorded" here.
  const equityLineage = lineageByField.get('total_equity')
  const stackModel = buildCapitalStackModel({
    get: v,
    raw,
    structure: null,
    documents: [],
    activity: [],
    notes: [],
    equityDerivation:
      equityLineage && stateOfLineage(equityLineage) === 'derived'
        ? { formula: equityLineage.derivation?.formula ?? null }
        : null,
    propertyValueDerived: stateOfLineage(lineageByField.get('property_value')) === 'derived',
  })
  const stackSum = stackModel.totalCap ?? 0
  const capitalStack: CapitalStackRow[] = stackModel.layers.map((layer) => ({
    component: layer.position,
    lender: layer.lender,
    requested: formatAmount(layer.amount),
    // No recommendation is recorded anywhere on a deal; an approval exists
    // only after a committee has decided.
    recommended: DASH,
    approved: !approval.decided ? NOT_YET_APPROVED : layer.isRequested ? approvedText : DASH,
    amountRaw: layer.amount,
    pctStack: stackSum > 0 ? `${((layer.amount / stackSum) * 100).toFixed(1)}%` : null,
    pricing: layer.isDebt ? textOrDash(layer.rateLabel ?? g('rate') ?? loan?.rate) : DASH,
  }))

  const conditions = triggers.map((t) => ({
    label: t.description ?? t.type,
    owner: assignee !== DASH ? assignee : null,
    status: t.status === 'overdue' ? ('overdue' as const) : t.status === 'resolved' ? ('cleared' as const) : ('open' as const),
    due: t.due ? formatDate(t.due) : null,
    source: t.type || null,
  }))

  const credit = readCreditNarrative(raw)
  const narrativeRisks: DossierWorkspaceModel['risks'] = credit.risks
  const triggerRisks: DossierWorkspaceModel['risks'] = triggers
    .filter((t) => t.status !== 'resolved')
    .map((t) => ({
      risk: t.description ?? t.type,
      mitigation: DASH,
      severity: severityOf(t.severity),
    }))
    .filter((row) => !narrativeRisks.some((item) => item.risk === row.risk))
  const risks = [...narrativeRisks, ...triggerRisks].slice(0, 8)
  const strengths = credit.strengths

  const exceptions: DossierWorkspaceModel['exceptions'] = triggers
    .filter((t) => t.status === 'overdue' || t.status === 'pending' || t.status === 'upcoming')
    .map((t) => ({
      severity: severityOf(t.severity),
      label: t.description ?? t.type,
      owner: assignee !== DASH ? assignee : null,
      due: t.due ? formatDate(t.due) : null,
    }))

  const parties = [
    // Sponsor and borrower are one party. Two rows carrying the same name is
    // what the merge was asked to remove.
    { role: 'Sponsor / Borrower', name: textOrDash(g('sponsor') ?? loan?.sponsor ?? g('borrower') ?? loan?.borrower), contact: null, lastCommitment: null, nextCommitment: null },
    { role: 'Broker', name: textOrDash(g('broker_name') ?? loan?.brokerName, DASH), contact: null, lastCommitment: null, nextCommitment: null },
  ]

  const dealTeam =
    assignee && assignee !== DASH
      ? [
          {
            name: assignee,
            role: 'Deal Owner',
            from: originated ? formatDate(originated) : null,
            status: 'Active',
            involvementPct: 100,
            docCount: documents.length,
          },
        ]
      : []

  const contacts: DossierWorkspaceModel['contacts'] = [
    {
      role: 'Broker',
      name: textOrDash(g('broker_name') ?? loan?.brokerName, ''),
      phone: textOrDash(g('broker_phone'), '') || null,
      email: textOrDash(g('broker_email') ?? g('broker_contact_email'), '') || null,
    },
    {
      role: 'Sponsor',
      name: textOrDash(g('sponsor') ?? loan?.sponsor, ''),
      phone: textOrDash(g('sponsor_phone'), '') || null,
      email: textOrDash(g('sponsor_email'), '') || null,
    },
  ].filter((c) => c.name)

  const nextActions = openTriggers.slice(0, 8).map((t) => ({
    action: t.description ?? t.type,
    owner: assignee !== DASH ? assignee : null,
    due: t.due ? formatDate(t.due) : null,
  }))

  // Every authenticated API call is audited as `request`, and those rows carry
  // the loan id in their query string — so "somebody opened this page" was
  // being listed in the deal's ledger beside real decisions. A page view is not
  // a stage event, and dropping them removes most of what read as gibberish.
  const journalFromActivity: DossierWorkspaceModel['journal'] = activity
    .filter((e) => isDealEvent(e.eventType))
    .slice(0, 20)
    .map((e, i) => ({
      id: String(e.id ?? i),
      // Kept RAW so the ledger can split it into date and time columns; it used
      // to be pre-formatted here, which also broke the sort below.
      at: e.createdAt ?? '',
      kind: e.eventType ?? 'activity',
      title: humanizeEvent(e.eventType),
      summary: describeEvent(e.details),
      actor: e.userEmail,
      attachments: [],
    }))

  const journalFromNotes = notes.map((n) => ({
    id: n.id,
    at: n.createdAt ?? '',
    kind: 'note',
    title: 'Note',
    summary: n.body.slice(0, 200),
    actor: n.author,
    attachments: [],
  }))

  const journal = [...journalFromNotes, ...journalFromActivity]
    .sort((a, b) => {
      const ta = Date.parse(a.at) || 0
      const tb = Date.parse(b.at) || 0
      return tb - ta
    })
    .slice(0, 25)

  // Communications are the exchanges with the outside world — the source email
  // and anything the desk logged by hand. They were previously the first eight
  // JOURNAL rows relabelled, which is why they showed audit JSON and named
  // neither sender nor recipient.
  const loggedComms: DossierWorkspaceModel['communications'] = journal
    .filter((j) => COMMUNICATION_KINDS.has(j.kind))
    .slice(0, 8)
    .map((j) => ({
      at: j.at,
      kind: humanizeEvent(j.kind),
      actor: j.actor,
      subject: j.summary ?? j.title,
      from: j.actor,
      to: null,
    }))

  // The email the deal came from: a real sender, real recipients, a real
  // subject. `sourceEmail` is the envelope carried on the intake record.
  const inbound: DossierWorkspaceModel['communications'] = (input.sourceEmails ?? []).map((m) => ({
    at: m.receivedAt ?? '',
    kind: 'Email',
    actor: m.from ?? null,
    subject: m.subject ?? '(no subject)',
    from: m.from ?? null,
    to: (m.to ?? []).join(', ') || null,
  }))

  const communications = [...inbound, ...loggedComms]
    .sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0))
    .slice(0, 8)

  const historicNoi = asNumber(loan?.cashFlows?.historicNOI)
  const currentNoi = asNumber(v('noi') ?? loan?.cashFlows?.prospectiveNOI)
  const underwritingChanges: DossierWorkspaceModel['underwritingChanges'] = []
  if (historicNoi != null && currentNoi != null) {
    const delta = pctChange(historicNoi, currentNoi)
    underwritingChanges.push({
      metric: 'NOI',
      entry: formatMoney(historicNoi),
      current: formatMoney(currentNoi),
      change: delta.text,
      direction: delta.direction,
      source: 'Cash flows on record',
    })
  }

  const decision = {
    headline: creditDecision(stage),
    date: null as string | null,
    body:
      String(stage ?? '') === 'IC / Committee Approved'
        ? 'Credit decision reflects the current pipeline stage on this record.'
        : null,
    decided: approval.decided,
    approvedAmount: approvedText,
    maxLtv: maxLtvText,
    // A decision's rate exists only once a committee has decided, like the
    // amount and max LTV beside it. Before that the rate on the record is the
    // request's, and it reads on the Loan Overview as the coupon.
    rate: approval.decided ? textOrDash(g('approved_rate') ?? g('rate') ?? loan?.rate) : NOT_YET_APPROVED,
    conditionsOpen: openTriggers.length,
    conditionsCleared: clearedTriggers.length,
    nextGate: openTriggers[0]?.description ?? openTriggers[0]?.type ?? null,
  }

  // The computed cumulative age, else the sum of the stage dwell segments — never the stored `daysOpen`
  // (0 on every deal), which would outrank the segments and print "0 days" on a deal that is weeks old.
  const totalAgeDays =
    asNumber(loan?.totalDaysOpen) ??
    (segments.length > 0 ? segments.reduce((n, s) => n + (s.days ?? 0), 0) : null)

  const targetClose = formatDate(g('target_close'))

  const obligations = triggers.map((t) => ({
    label: t.description ?? t.type,
    owner: assignee !== DASH ? assignee : null,
    externalParty: null,
    due: t.due ? formatDate(t.due) : null,
    aging: t.status === 'overdue' ? 'Overdue' : t.status,
    status: t.status,
  }))

  const recentSources = [
    ...documents.map((d) => ({
      filename: d.filename,
      provider: d.uploadedBy ?? null,
      verified: true,
      uploadedAt: d.createdAt ?? null,
    })),
    ...sourceFiles.map((f) => ({
      filename: f.filename,
      provider: f.kind.replace(/_/g, ' '),
      verified: false,
      // The message it arrived on, so a source list reads through time
      // alongside the uploaded documents rather than sitting undated.
      uploadedAt: f.receivedAt,
    })),
  ].slice(0, 8)

  const metricLineage = (id: KpiMetricId): MetricLineage | null => {
    const kpi = kpis.find((k) => k.id === id)
    if (!kpi) return null
    const spec = METRIC_FORMULAS[id]
    const operands = (spec?.inputs ?? []).map(({ label, field }) => ({
      label,
      value: formatFieldValue(field, v(field), defs),
      source: sourceFromLineage(field, lineageByField, attribution),
    }))
    const fieldRow = kpi.fieldName ? lineageByField.get(kpi.fieldName) : undefined
    const history = fieldRow?.history?.[0]
    return {
      metricId: id,
      label: kpi.label,
      displayValue: kpi.value,
      formula: spec?.formula ?? input.derivationNotes[kpi.fieldName ?? ''] ?? null,
      operands,
      enteredBy: fieldRow?.editorEmail ?? history?.editorEmail ?? null,
      // Confirmed means a PERSON — a machine backfill wrote `approved` too,
      // so the agreed state decides, never the raw word.
      verifiedBy: fieldRow?.state === 'confirmed' ? fieldRow.confirmation?.by ?? fieldRow.editorEmail : null,
      historyNote: history?.reason ?? null,
      deterministic: Boolean(spec || input.derivationNotes[kpi.fieldName ?? '']),
    }
  }

  return {
    loanId,
    propertyName,
    address,
    assetType,
    priority: priority || null,
    headerMeta,
    relationshipOwner: assignee !== DASH ? assignee : null,
    targetClose: targetClose === DASH ? null : targetClose,
    totalAgeDays,
    creditState: textOrDash(stage),
    pipelineStages: buildPipelineStages(
      String(stage ?? ''),
      assignee !== DASH ? assignee : null,
      segments,
      originated ? String(originated) : null,
    ),
    kpis,
    loanOverview,
    propertyFields,
    capitalStack,
    capitalStackTotal: stackSum > 0 ? formatAmount(stackSum) : null,
    // A total whose equity leg is implied from the stated LTV is loan / LTV —
    // a figure no document records (rule R3) — and one built on a derived
    // property value is no more recorded than that value: never "recorded".
    capitalStackTotalLabel: stackModel.totalImplied ? 'Implied total' : 'Recorded total',
    capitalStackNote: stackModel.layers.length > 0 ? stackModel.stackDisclosure : null,
    // The covenants a term sheet states, in their own catalogue fields.
    covenants: [
      isPresent(v('covenant_min_dscr')) ? `Min DSCR ${formatMultiple(v('covenant_min_dscr'))}` : null,
      isPresent(v('covenant_max_ltv')) ? `Max LTV ${formatPoints(v('covenant_max_ltv'))}` : null,
      isPresent(v('covenant_min_debt_yield')) ? `Min Debt Yield ${formatPoints(v('covenant_min_debt_yield'))}` : null,
    ].filter((c): c is string => c != null),
    covenantSource: sourceFromLineage(
      ['covenant_min_dscr', 'covenant_max_ltv', 'covenant_min_debt_yield'].find((name) => isPresent(v(name))) ??
        'covenant_min_dscr',
      lineageByField,
      attribution,
    ),
    conditions,
    risks,
    memoRisks: narrativeRisks,
    strengths,
    exceptions,
    parties,
    dealTeam,
    contacts,
    nextActions,
    communications,
    underwritingChanges,
    decision,
    journal,
    obligations,
    recentSources,
    tabCounts: {
      // What the Documents tab actually lists: current vault documents plus
      // the originals the deal arrived with, deduped the same way that page
      // dedupes them. Counting raw rows here badged a two-version file as two
      // documents and ignored the intake package entirely.
      documents: buildDocumentsModel(documents, triggers, { files: sourceFiles }).totalDocuments,
      // Each badge counts the rows its own tab lists. Journal counted every
      // activity row (page views included) plus notes, and Audit counted
      // lineage history on the tracked fields — so The Marlowe read AUDIT (1)
      // beside an Audit tab listing three events.
      journal: journal.length,
      // The Audit tab's Trail and Events stat list the stage-audit chain
      // (DJA-05), which holds field changes, notes and arrivals the activity
      // feed does not — The Marlowe read AUDIT (4) beside 11 events.
      audit: input.stageAudit ? stageAuditEntries(input.stageAudit).length : activity.length,
    },
    metricLineage,
  }
}
